import { AGGREGATORS, DOOK_TENANT_TYPES } from 'src/constants/aggregators';
import { PAYMENT_TYPES } from 'src/constants/main';
import { ORDER_DINE_IN_TYPE, ORDER_STATUS } from 'src/constants/order';

export enum OrderDeliveryType {
  Delivery = 'DELIVERY',
  DineIn = 'DINE_IN',
  Curbside = 'CURBSIDE',
  Pickup = 'PICKUP',
}

export enum OrderingChannelType {
  Admin = 'ADMIN',
  Android = 'ANDROID',
  App = 'APP',
  Careem = 'CAREEM',
  Dook = 'DOOK',
  MiniDook = 'MINI_DOOK',
  EMenu = 'EMENU',
  IOS = 'IOS',
  Jahez = 'JAHEZ',
  HungerStation = 'HUNGER_STATION',
  ToYou = 'TO_YOU',
  Keeta = 'KEETA',
  Kiosk = 'KIOSK',
  Web = 'WEB',
}

export enum OrderPaymentType {
  Terminal = 'TERMINAL',
  Card = 'CARD',
  Cash = 'CASH',
  Careem = 'CAREEM',
  Jahez = 'JAHEZ',
  HungerStation = 'HUNGER_STATION',
  ToYou = 'TO_YOU',
  Keeta = 'KEETA',
  DookCash = 'DOOK_CASH',
  DookOnline = 'DOOK_ONLINE',
}

const orderingChannelMap: { [key: string]: OrderingChannelType } = {
  a: OrderingChannelType.Admin,
  u: OrderingChannelType.Web,
  m: OrderingChannelType.App,
  i: OrderingChannelType.IOS,
  d: OrderingChannelType.Android,
  k: OrderingChannelType.Kiosk,
};

const orderingChannelAggregatorMap: { [key: string]: OrderingChannelType } = {
  [AGGREGATORS.JAHEEZ]: OrderingChannelType.Jahez,
  [AGGREGATORS.CAREEM]: OrderingChannelType.Careem,
  [AGGREGATORS.DOOK]: OrderingChannelType.Dook,
  [AGGREGATORS.MINI_DOOK]: OrderingChannelType.MiniDook,
  [AGGREGATORS.HUNGER_STATION]: OrderingChannelType.HungerStation,
  [AGGREGATORS.TO_YOU]: OrderingChannelType.ToYou,
  [AGGREGATORS.KEETA]: OrderingChannelType.Keeta,
};

// eslint-disable-next-line unused-imports/no-unused-vars
export const getOrderingChannel = (createdBy: string, serviceKey: number, dineInDeliveryType: any): OrderingChannelType => {
  return (
    // (orderingChannelMap[createdBy] !== OrderingChannelType.KioskItem && !serviceKey && !!dineInDeliveryType && OrderingChannelType.EMenu) ||
    orderingChannelMap[createdBy] || orderingChannelAggregatorMap[serviceKey] || OrderingChannelType.Web
  );
};

export const getOrderPaymentType = (paymentType: PAYMENT_TYPES, serviceKey: AGGREGATORS): OrderPaymentType => {
  const DOOK_AGGREGATORS = [AGGREGATORS.DOOK, AGGREGATORS.MINI_DOOK];
  if (DOOK_AGGREGATORS.includes(serviceKey) && paymentType === PAYMENT_TYPES.CASH) {
    return OrderPaymentType.DookCash;
  }

  if (DOOK_AGGREGATORS.includes(serviceKey) && paymentType === PAYMENT_TYPES.ONLINE_PAYMENT) {
    return OrderPaymentType.DookOnline;
  }

  switch (serviceKey) {
    case AGGREGATORS.JAHEEZ:
      return OrderPaymentType.Jahez;
    case AGGREGATORS.CAREEM:
      return OrderPaymentType.Careem;
    case AGGREGATORS.HUNGER_STATION:
      return OrderPaymentType.HungerStation;
    case AGGREGATORS.TO_YOU:
      return OrderPaymentType.ToYou;
    case AGGREGATORS.KEETA:
      return OrderPaymentType.Keeta;
    default: {
      switch (paymentType) {
        case PAYMENT_TYPES.TERMINAL:
          return OrderPaymentType.Terminal;
        case PAYMENT_TYPES.ONLINE_PAYMENT:
          return OrderPaymentType.Card;
        default:
          return OrderPaymentType.Cash;
      }
    }
  }
};

export const getOrderDeliveryType = (deliveryType: any, dineInDeliveryType: any, isCurbside: any) => {
  if (deliveryType && String(deliveryType).startsWith('d')) {
    return OrderDeliveryType.Delivery;
  } else if (dineInDeliveryType === ORDER_DINE_IN_TYPE.DINE_IN) {
    return OrderDeliveryType.DineIn;
  } else if (Boolean(isCurbside)) {
    return OrderDeliveryType.Curbside;
  }
  return OrderDeliveryType.Pickup;
};

export const orderCast = (data: any) => {
  if (
    data.payment_type === PAYMENT_TYPES.JAHEZ &&
    (data.dook_payment_type === PAYMENT_TYPES.CASH || data.dook_payment_type === PAYMENT_TYPES.ONLINE_PAYMENT)
  ) {
    data.service_key = data.dook_tenant_type === DOOK_TENANT_TYPES.MINI_DOOK ? AGGREGATORS.MINI_DOOK : AGGREGATORS.DOOK;
    data.payment_type = data.dook_payment_type;
  }

  return {
    id: String(data.id),
    branch: { id: Number(data.branch?.id) },
    orderKey: String(data.order_key),
    branchName: String(data.branch_name || ''),
    customerPhone: String(data.customer_mobile),
    status: String(data.order_status) as ORDER_STATUS,
    createdAt: data.created_at,
    totalPrice: Number(data.order_total),
    orderingChannel: getOrderingChannel(String(data.created_by), data.service_key, data.dinein_delivery_type),
    deliveryType: getOrderDeliveryType(data.delivery_type, data.dinein_delivery_type, data.is_curbside),
    paymentType: getOrderPaymentType(data.payment_type, data.service_key),
    createdBy: String(data.created_by),
  };
};

export const ordersCast = (data: any[]) => {
  const orders = data.map(orderCast);
  return new Map(orders.map((order) => [order.id, order]));
};

export type TOrder = ReturnType<typeof orderCast>;

export enum OrdersHistoryTableColumns {
  OrderID = 'order_id',
  BranchName = 'branch_name',
  CustomerPhone = 'customer_phone',
  Status = 'status',
  CreatedAt = 'created_at',
  Details = 'details',
  TotalPrice = 'total_price',
}

export const orderColorByStatus = {
  [ORDER_STATUS.DRAFT]: '#429EFF',
  [ORDER_STATUS.WAITING_FOR_PAYMENT]: '#FFD130',
  [ORDER_STATUS.PENDING]: '#429EFF',
  [ORDER_STATUS.ACCEPTED]: '#429EFF',
  [ORDER_STATUS.PROCESSING]: '#429EFF',
  [ORDER_STATUS.PICKEDUP]: '#429EFF',
  [ORDER_STATUS.OUT_FOR_DELIVERY]: '#429EFF',
  [ORDER_STATUS.ASSIGNED]: '#429EFF',
  [ORDER_STATUS.WAITING_FOR_PICKUP]: '#429EFF',
  [ORDER_STATUS.COMPLETED]: '#78B59F',
  [ORDER_STATUS.RETURNED]: '#FFA775',
  [ORDER_STATUS.CANCELED]: '#FF7676',
  [ORDER_STATUS.ON_HOLD]: '#FFD130',
};
